import type { ReactNode } from 'react';
import { useAuth } from '../../auth/useAuth';
import { AuthStatus } from '../AuthStatus';

/**
 * The route guard for authenticated surfaces (U1a).
 *
 * The application had none before this. Every authenticated page gated itself
 * inside its own render — `ReportLibrary` and `ReportPage` each check `status`
 * and return a sign-in prompt instead of their content. That works, but it puts
 * an auth decision in every page that ever needs one, and the next page to be
 * written is the one that forgets. This centralises the decision without
 * changing its shape: the existing pages keep their own gates, and new
 * authenticated routes wrap in this instead of reimplementing it.
 *
 * **It gates by rendering, not by redirecting.** A redirect would have to fire
 * while `status` is still `loading`, before the store has resolved a session
 * from storage, so an arriving signed-in visitor would be bounced away from the
 * page they asked for and then have to find their way back. Rendering a gate
 * costs one frame and gets the same protection.
 *
 * **`password-recovery` is not signed in, and that is the point of this
 * component.** A recovery session carries a valid access token: it arrived from
 * an emailed link, so it proves control of the mailbox and nothing else. It is
 * not evidence the holder ever knew the password. `ReportLibrary` derives its
 * token as `status === 'signed-in' ? … : null` for exactly this reason, and the
 * gate below is the same test rather than a truthiness check on the session.
 * A `session != null` check here would hand every authenticated surface to
 * anyone holding a password-reset link.
 *
 * `loading` renders nothing, matching `AccountAffordance`: a spinner in this
 * position flashes on every arrival and says nothing the visitor can act on.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();

  if (status === 'loading') return null;

  if (status === 'password-recovery') {
    return (
      <section className="flex flex-col gap-3" data-testid="require-auth-recovery">
        <p className="text-foreground">
          Finish setting your new password before continuing.
        </p>
        {/* `AuthStatus` renders sign-out in this state, which is the only exit.
            Without it a recovery session has no way out but clearing storage. */}
        <AuthStatus />
      </section>
    );
  }

  if (status !== 'signed-in') {
    return (
      <section className="flex flex-col gap-3" data-testid="require-auth-signed-out">
        <p className="text-foreground">Sign in to see your dashboard.</p>
        <AuthStatus />
      </section>
    );
  }

  return <>{children}</>;
}
